import * as React from 'react';
import styled from '@emotion/styled';

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onClick'> {
  label?: React.ReactNode;
  /** Mixed state for "select all" controls. */
  indeterminate?: boolean;
  /** Handled on the label wrapper — see the note in the component below. */
  onClick?: React.MouseEventHandler<HTMLLabelElement>;
}

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.color.fg.default};
  cursor: pointer;
  user-select: none;
`;

const Native = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

/* Figma DS Checkbox (102-2022): 22px box, radius 4, slate/500 border,
 * solid primary when checked with a 16px white check. */
const Box = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.color.slate[500]};
  background: ${({ theme }) => theme.color.bg.default};
  display: grid;
  place-items: center;
  flex-shrink: 0;
  transition: all ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.ease};

  svg { width: 16px; height: 16px; color: #fff; opacity: 0; transition: opacity ${({ theme }) => theme.motion.fast}; }

  ${Label}:hover & { border-color: ${({ theme }) => theme.color.primary.base}; }
  ${Native}:checked + &,
  ${Native}:indeterminate + & {
    background: ${({ theme }) => theme.color.primary.base};
    border-color: ${({ theme }) => theme.color.primary.base};
  }
  ${Native}:checked + & svg,
  ${Native}:indeterminate + & svg { opacity: 1; }
  ${Native}:focus-visible + & { box-shadow: 0 0 0 3px ${({ theme }) => theme.color.blue[100]}; }
  ${Native}:disabled + & {
    background: ${({ theme }) => theme.color.bg.muted};
    border-color: ${({ theme }) => theme.color.border.default};
  }
`;

/** Lumen checkbox — multi-select / standalone toggle (no immediate save). */
export function Checkbox({
  label,
  children,
  className,
  indeterminate = false,
  onClick,
  ...rest
}: CheckboxProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useLayoutEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    // onClick lives on the label, not the input: clicking the box fires two
    // click events (the label's own and the one it forwards to the input),
    // and both bubble through the label — so callers that stop propagation
    // (e.g. rows that toggle on click) must intercept them here or the
    // toggle happens twice and cancels itself out.
    <Label className={className} onClick={onClick}>
      <Native ref={inputRef} type="checkbox" {...rest} />
      <Box>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round">
          {indeterminate ? <path d="M5 12h14" /> : <path d="m5 12 5 5 9-11" />}
        </svg>
      </Box>
      {label ?? children}
    </Label>
  );
}
